// TouchController.js
// Handles touch-specific interactions: library touch drag, touch device detection
// Lower-level touch processing that works alongside GestureController

import { getTouchCoords, getPointerPos } from '../utils/coords.js';

// ============= STATE =============
// Library touch drag state (for iOS/touch devices)
let libraryTouchDragState = null;
let libraryTouchDragPreview = null;

// Touch device detection (cached at load)
let isTouchDevice = false;

// ============= DEPENDENCIES (injected via init) =============
let deps = {
    definitionModel: null,
    favoritesService: null,
    screenToCanvas: null,
    createInstanceFromTemplate: null,
    renderLibrary: null,
    startLongPressTimer: null,
    cancelLongPress: null,
    isDraggingDevice: null,
    isDraggingCable: null,
    handlePointerMove: null,
    handlePointerUp: null,
    getCanvasPanState: null
};

// ============= INITIALIZATION =============
export function initTouchController(dependencies) {
    deps = { ...deps, ...dependencies };
    
    // Detect touch device
    isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    
    // Set up global document touch handlers for library drag
    setupGlobalTouchHandlers();
}

// ============= GETTERS =============
export function getIsTouchDevice() {
    return isTouchDevice;
}

export function getLibraryTouchDragState() {
    return libraryTouchDragState;
}

export function isLibraryTouchDragActive() {
    return libraryTouchDragState !== null;
}

// ============= LIBRARY TOUCH DRAG =============
export function startLibraryTouchDrag(templateId, startX, startY, fromFavorites = false) {
    // Get template from appropriate source
    const template = fromFavorites 
        ? deps.favoritesService.get(templateId) 
        : deps.definitionModel.getById(templateId);
    if (!template) return;
    
    // IMPORTANT: Disable scrolling on library containers during drag
    // This prevents iOS from stealing touch events for scrolling
    const libraryList = document.getElementById('library-list');
    const libraryPanel = document.getElementById('library-panel');
    if (libraryList) libraryList.style.overflowY = 'hidden';
    if (libraryPanel) libraryPanel.style.overflowY = 'hidden';
    
    libraryTouchDragState = {
        templateId,
        startX,
        startY,
        isDragging: false,
        fromFavorites
    };
}

export function updateLibraryTouchDrag(clientX, clientY) {
    if (!libraryTouchDragState) return;
    
    // Check if we've moved enough to start actual dragging
    const dx = clientX - libraryTouchDragState.startX;
    const dy = clientY - libraryTouchDragState.startY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (!libraryTouchDragState.isDragging && dist > 15) {
        // Start the actual drag - create preview
        libraryTouchDragState.isDragging = true;
        
        // Get template from appropriate source
        const template = libraryTouchDragState.fromFavorites
            ? deps.favoritesService.get(libraryTouchDragState.templateId)
            : deps.definitionModel.getById(libraryTouchDragState.templateId);
        if (template) {
            libraryTouchDragPreview = document.createElement('div');
            libraryTouchDragPreview.className = 'library-drag-preview';
            libraryTouchDragPreview.textContent = template.name;
            // Set initial position immediately
            libraryTouchDragPreview.style.left = (clientX + 10) + 'px';
            libraryTouchDragPreview.style.top = (clientY + 10) + 'px';
            document.body.appendChild(libraryTouchDragPreview);
            
            // Mark source item as dragging
            const sourceItem = document.querySelector(`.library-item[data-template-id="${libraryTouchDragState.templateId}"]`);
            if (sourceItem) sourceItem.classList.add('dragging');
        }
    }
    
    // Update preview position
    if (libraryTouchDragState.isDragging && libraryTouchDragPreview) {
        libraryTouchDragPreview.style.left = (clientX + 10) + 'px';
        libraryTouchDragPreview.style.top = (clientY + 10) + 'px';
    }
}

export function endLibraryTouchDrag(clientX, clientY) {
    if (!libraryTouchDragState) return;
    
    const wasDragging = libraryTouchDragState.isDragging;
    const templateId = libraryTouchDragState.templateId;
    const fromFavorites = libraryTouchDragState.fromFavorites;
    
    // Clean up preview
    if (libraryTouchDragPreview) {
        libraryTouchDragPreview.remove();
        libraryTouchDragPreview = null;
    }
    
    // Remove dragging class from source item
    const sourceItem = document.querySelector(`.library-item[data-template-id="${templateId}"]`);
    if (sourceItem) sourceItem.classList.remove('dragging');
    
    // Check if dropped on canvas
    if (wasDragging) {
        const canvasContainer = document.querySelector('.canvas-container');
        const canvasRect = canvasContainer.getBoundingClientRect();
        
        if (clientX >= canvasRect.left && clientX <= canvasRect.right &&
            clientY >= canvasRect.top && clientY <= canvasRect.bottom) {
            
            // If from favorites, add to local first if not already there
            if (fromFavorites) {
                const existsInLocal = deps.definitionModel.exists(templateId);
                if (!existsInLocal) {
                    const favTemplate = deps.favoritesService.get(templateId);
                    if (favTemplate) {
                        deps.definitionModel.add(JSON.parse(JSON.stringify(favTemplate)));
                    }
                }
            }
            
            // Dropped on canvas - create instance
            const pos = deps.screenToCanvas(clientX, clientY);
            deps.createInstanceFromTemplate(templateId, pos);
            deps.renderLibrary();
        }
    }
    
    // Restore scrolling on library containers
    const libraryList = document.getElementById('library-list');
    const libraryPanel = document.getElementById('library-panel');
    if (libraryList) libraryList.style.overflowY = '';
    if (libraryPanel) libraryPanel.style.overflowY = '';
    
    libraryTouchDragState = null;
}

export function cancelLibraryTouchDrag() {
    if (!libraryTouchDragState) return;
    
    // Clean up preview
    if (libraryTouchDragPreview) {
        libraryTouchDragPreview.remove();
        libraryTouchDragPreview = null;
    }
    
    // Remove dragging class from source item
    const sourceItem = document.querySelector(`.library-item[data-template-id="${libraryTouchDragState.templateId}"]`);
    if (sourceItem) sourceItem.classList.remove('dragging');
    
    // Restore scrolling on library containers
    const libraryList = document.getElementById('library-list');
    const libraryPanel = document.getElementById('library-panel');
    if (libraryList) libraryList.style.overflowY = '';
    if (libraryPanel) libraryPanel.style.overflowY = '';
    
    libraryTouchDragState = null;
}

// ============= GLOBAL TOUCH EVENT HANDLERS =============
function setupGlobalTouchHandlers() {
    // Global touchstart handler - handles library item drag initiation
    document.addEventListener('touchstart', (e) => {
        const libraryItem = e.target.closest('.library-item');
        if (libraryItem) {
            // Don't intercept touches on the favorite star button - let it handle its own events
            if (e.target.closest('.library-item-favorite')) {
                return;
            }
            
            // Prevent iOS native behaviors (context menu, scroll start)
            e.preventDefault();
            
            // Start long-press timer for context menu
            if (deps.startLongPressTimer) {
                deps.startLongPressTimer(e, libraryItem, 'library-item');
            }
            
            // Start tracking for touch drag
            const coords = getTouchCoords(e, 0);
            const templateId = libraryItem.dataset.templateId;
            const fromFavorites = libraryItem.dataset.source === 'favorites';
            if (templateId && coords) {
                startLibraryTouchDrag(templateId, coords.x, coords.y, fromFavorites);
            }
        }
    }, { passive: false });
    
    document.addEventListener('touchmove', (e) => {
        const isDragging = deps.isDraggingDevice ? deps.isDraggingDevice() : false;
        const isCableDrag = deps.isDraggingCable ? deps.isDraggingCable() : false;
        const canvasPan = deps.getCanvasPanState ? deps.getCanvasPanState() : null;
        
        if (isDragging || isCableDrag || canvasPan) {
            e.preventDefault();
        }
        
        // Handle library touch drag
        if (libraryTouchDragState) {
            // IMPORTANT: Prevent default immediately to stop iOS scroll behavior
            e.preventDefault();
            // Cancel long-press since we're moving
            if (deps.cancelLongPress) {
                deps.cancelLongPress();
            }
            const coords = getTouchCoords(e, 0);
            if (coords) updateLibraryTouchDrag(coords.x, coords.y);
        }
        
        if (deps.handlePointerMove) {
            deps.handlePointerMove(e);
        }
    }, { passive: false });
    
    document.addEventListener('touchend', (e) => {
        // Cancel long-press on any touchend
        if (deps.cancelLongPress) {
            deps.cancelLongPress();
        }
        
        // Handle library touch drag end
        if (libraryTouchDragState) {
            const pos = getPointerPos(e); // Uses changedTouches for touchend
            endLibraryTouchDrag(pos.clientX, pos.clientY);
        }
        
        if (deps.handlePointerUp) {
            deps.handlePointerUp(e);
        }
    });
    
    document.addEventListener('touchcancel', (e) => {
        cancelLibraryTouchDrag();
        if (deps.cancelLongPress) {
            deps.cancelLongPress();
        }
        if (deps.handlePointerUp) {
            deps.handlePointerUp(e);
        }
    });
}
